import { platformLabel as labelForPlatform } from 'components/PlatformIcon'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { useAnalytics } from 'hooks'
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import { Button } from 'components/Button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import { flushAllPendingPersists, useCreativeStore } from '../store/creative-store'
import { useCalendarStore } from '../../calendar/store/calendar-store'
import { creativeTypeToPlatform, useIdeaStore, type LivePlatform } from '../store/idea-store'
import type { Creative } from '../types'
import { isSyntheticPostIdea, syntheticProjectIdToCreativeId } from '../selectors/posts-page-items'
import { ComposerHeader } from '../components/ComposerHeader'
import { ComposerShell } from '../components/ComposerShell'
import { apiCall } from 'modules/core/services/api/api'
import { usePlatformRolloutEnabled } from 'modules/feature-flags'
import { CreativeService } from 'openapi/services/CreativeService'
import { usePlatformConnections } from '../hooks/usePlatformConnections'
import { useDeriveSiblings } from '../hooks/useDeriveSiblings'
import { defaultPlatformSeed, useEnsureAutopilotPlansLoaded } from '../utils/default-platform-seed'
import type { PostEntrySource } from '../utils/post-entry-source'

// Mirror the strip's labels so the remove-confirm dialog reads naturally.

// Stable reference for the empty-siblings case so the zustand selector below
// doesn't return a fresh `{}` on every render (which would force ComposerPage
// to re-render whenever any unrelated slice of useIdeaStore changes).
const EMPTY_SIBLINGS: Partial<Record<LivePlatform, Creative>> = {}

// Stable empty array so the project-creatives selector below doesn't hand back a
// fresh `[]` each render (which would thrash the zustand selector).
const EMPTY_CREATIVES: Creative[] = []

/**
 * Ephemeral hand-off payload passed via React Router `location.state` from
 * surfaces that hold transient UI state (PostModal) to the Composer. IdeaCard
 * has no local state and passes nothing — the Composer falls back to server
 * hydration (idea.confirmedPlatforms / creative.scheduledAt).
 */
export interface ComposerNavState {
  /** Platforms the user has staged for publish. Seeds the in-strip selection,
   *  overriding `idea.confirmedPlatforms`. */
  activePlatforms?: LivePlatform[]
  /** ISO 8601 date+time staged in PostModal's editors. Pre-fills the Schedule
   *  dialog only — does NOT flip the committed "Scheduled for X" pill. */
  scheduledAt?: string
  /** Free-form analytics source tag — the proximate hop into Composer
   *  ('post_modal', 'calendar', 'weekly_cockpit', 'assistant'). */
  source?: string
  /** Journey origin for `post_scheduled.entry_source`, preserved across the
   *  PostModal ↔ Composer round-trip so the eventual Post Scheduled emit can
   *  attribute back to where the user originally started this intent. */
  entry_source?: PostEntrySource
}

/**
 * Composer is the **Studio of a Post**. Every creative belongs to a Project,
 * so the canonical URL is `/compose/:projectId/:platform?` — one shell, one
 * project, N platform tabs. `:platform` defaults to the post's primary
 * platform and is auto-promoted into the URL on first paint.
 *
 * Legacy `/compose/:creativeId` links still resolve: we round-trip
 * `loadIdea(parentId)` and replace the URL with the project form.
 *
 * Resolution order on a bare `/compose/:id` hit:
 *   1. creative-store hit → auto-promote URL to `/compose/:projectId/:platform`
 *      (replace, no history entry).
 *   2. miss → `loadIdea(id)` and treat as projectId.
 *   3. both fail → redirect home.
 */
export function ComposerPage() {
  const { id, platform: paramPlatform } = useParams<{ id: string; platform?: LivePlatform }>()
  const location = useLocation()
  const navigate = useNavigate()
  const { track } = useAnalytics()

  // ─── Store reads ─────────────────────────────────────────────────────────
  // /posts renders confirmed/standalone posts as synthetic cards whose
  // projectId is `creative:<creativeId>` (see posts-page-items). Resolve those
  // to the underlying creative so Edit promotes to the real project URL instead
  // of 404ing on a non-existent `creative:<id>` project (the "deleted" toast).
  const creativeLookupId =
    id && isSyntheticPostIdea(id) ? (syntheticProjectIdToCreativeId(id) ?? id) : id
  const creativeById = useCreativeStore(s =>
    creativeLookupId ? s.creatives.find(c => c.id === creativeLookupId) : undefined
  )
  const loaded = useCreativeStore(s => s.loaded)
  const loading = useCreativeStore(s => s.loading)
  const fetchCreatives = useCreativeStore(s => s.fetchCreatives)

  const idea = useIdeaStore(s => (id ? s.ideas.find(i => i.projectId === id) : undefined))
  const siblings = useIdeaStore(s =>
    id ? (s.siblingsByProject[id]?.byPlatform ?? EMPTY_SIBLINGS) : EMPTY_SIBLINGS
  )
  const loadIdea = useIdeaStore(s => s.loadIdea)

  // All creatives belonging to this project, mirrored into the creative store by
  // loadIdea / list hydration. Fallback source when the primary creative was
  // deleted (orphaned primaryCreativeId) or a platform's creative exists on the
  // project but wasn't seed-tagged as a sibling — so the active platform still
  // resolves to a real creative instead of stranding the page on a null primary.
  // Subscribe to the stable `creatives` array reference and derive the
  // per-project slice with useMemo. Filtering INSIDE the zustand selector
  // returned a fresh array every render (even when unchanged), which trips
  // useSyncExternalStore's "getSnapshot should be cached" guard and spins an
  // infinite render loop — the composer rendered a blank/black screen for
  // every post. Deriving outside the selector keeps the snapshot stable.
  const allCreatives = useCreativeStore(s => s.creatives)
  const projectCreatives = useMemo(
    () => (id ? allCreatives.filter(c => c.projectId === id) : EMPTY_CREATIVES),
    [allCreatives, id]
  )

  const [ideaLookupAttempted, setIdeaLookupAttempted] = useState(false)
  const [ideaLookupLoading, setIdeaLookupLoading] = useState(false)
  const promotedFromCreativeIdRef = useRef<string | null>(null)
  const { loading: connectionsLoading, connections } = usePlatformConnections()
  const isRolloutOn = usePlatformRolloutEnabled()
  const plansLoaded = useEnsureAutopilotPlansLoaded()
  const availablePlatforms = useMemo(() => {
    if (!idea) return [] as LivePlatform[]
    return idea.candidatePlatforms.filter(
      p => isRolloutOn(p) || p === idea.primaryPlatform || !!idea.confirmedPlatforms?.includes(p)
    )
  }, [idea, isRolloutOn])

  // In-strip platform selection. The set itself IS the publish set — no
  // separate checkbox state. Seeded once per Post from confirmedPlatforms
  // (or [primaryPlatform] as the floor). Order is preserved so newly-added
  // platforms append to the right.
  const [selectedPlatforms, setSelectedPlatforms] = useState<LivePlatform[]>([])
  const selectionSeededFor = useRef<string | null>(null)

  // Confirm-dialog state for removing a scheduled sibling.
  const [removeConfirm, setRemoveConfirm] = useState<{
    platform: LivePlatform
    siblingId: string
  } | null>(null)

  // Confirm-dialog state for "Reset to match post" on a customized variant.
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false)

  // Platforms unlocked through the "Customize for {platform}?" gate this
  // session — the view flips to the editable experience immediately; the
  // durable pin lands on the user's first committed edit (userEdit persist).
  const [customizeUnlocked, setCustomizeUnlocked] = useState<Set<LivePlatform>>(new Set())
  const [customizeGateOpen, setCustomizeGateOpen] = useState(false)

  useEffect(() => {
    setIdeaLookupAttempted(false)
    setIdeaLookupLoading(false)
    promotedFromCreativeIdRef.current = null
    selectionSeededFor.current = null
    setSelectedPlatforms([])
    setRemoveConfirm(null)
    setCustomizeUnlocked(new Set())
  }, [id])

  // ─── Initial fetch of standalone creatives list ──────────────────────────
  useEffect(() => {
    if (!loaded) fetchCreatives()
  }, [loaded, fetchCreatives])

  // ─── Flush pending creative-store debounces on page tear-down ────────────
  // `updateCreativeContent` schedules a 500ms debounced PUT. A user who
  // edits and refreshes within that window would lose the edit (the queued
  // request never fires). Send any pending PUTs via `fetch keepalive` so
  // they survive `pagehide` and `visibilitychange:hidden` (mobile background).
  useEffect(() => {
    const flush = () => flushAllPendingPersists()
    const onVis = () => {
      if (document.visibilityState === 'hidden') flush()
    }
    window.addEventListener('pagehide', flush)
    document.addEventListener('visibilitychange', onVis)
    return () => {
      window.removeEventListener('pagehide', flush)
      document.removeEventListener('visibilitychange', onVis)
    }
  }, [])

  // ─── Resolution: is :id a Post project, a creative, or unknown? ──────────
  // - If `id` matches an idea (already in store), we're in Post mode.
  // - Else if `id` matches a creative *with* a projectId whose project is a
  //   Post, auto-promote the URL to `/compose/:projectId/:platform`.
  // - Else attempt `loadIdea(id)`; on success it lands in `ideas`.
  useEffect(() => {
    if (!id) return
    if (idea) return // already a Post
    if (ideaLookupAttempted) return

    // Case A: creative-store hit. Decide if it belongs to a Post.
    if (creativeById?.projectId) {
      // Try loadIdea once to discover if the parent project is a Post.
      // We have to round-trip because the client-side ideas list is built
      // from Home/PostsPage queries and may not include this project yet.
      const parentId = creativeById.projectId
      if (promotedFromCreativeIdRef.current === creativeById.id) return
      promotedFromCreativeIdRef.current = creativeById.id
      setIdeaLookupAttempted(true)
      setIdeaLookupLoading(true)
      void loadIdea(parentId)
        .then(loadedIdea => {
          if (loadedIdea) {
            // One Post model: the primary IS the post (bare URL = editor);
            // a sibling promotes to its platform preview.
            const platform = creativeTypeToPlatform(creativeById.type)
            const isPrimary = loadedIdea.primaryCreative?.id === creativeById.id
            navigate(
              isPrimary || !platform ? `/compose/${parentId}` : `/compose/${parentId}/${platform}`,
              { replace: true }
            )
            return
          }
          // Null: the parent isn't a Post. Release the promote ref so the
          // zombie-render guard below stops holding the spinner and the page
          // falls through to the bounce, as it always did for this case.
          promotedFromCreativeIdRef.current = null
        })
        .finally(() => setIdeaLookupLoading(false))
      return
    }

    // Case B: creative-store miss AND `loaded` is true → maybe :id is a
    // projectId. Fire loadIdea once.
    if (loaded && !creativeById && !loading) {
      setIdeaLookupAttempted(true)
      // A synthetic `creative:<id>` has no backing project; with the creative
      // not in the store there's nothing to resolve — fall through to the
      // hard-miss toast rather than 404ing loadIdea on a non-existent project.
      if (isSyntheticPostIdea(id)) return
      setIdeaLookupLoading(true)
      void loadIdea(id).finally(() => setIdeaLookupLoading(false))
    }
  }, [id, idea, creativeById, loaded, loading, loadIdea, navigate, ideaLookupAttempted])

  // ─── Hard-miss toast ─────────────────────────────────────────────────────
  // After both lookups finish (creative-store hydrated AND loadIdea attempted)
  // with nothing resolved, the page silently <Navigate>'d home — the user got
  // bounced with no idea why. Most common cause: the post they're trying to
  // open was just deleted (theirs, or a teammate's). Surface a toast so the
  // jump is explained, gated on a ref so it fires once per resolution.
  const hardMissToastedRef = useRef(false)
  const hardMiss =
    !idea && !creativeById && loaded && !loading && ideaLookupAttempted && !ideaLookupLoading
  // Last resort before bouncing: the id may be a CREATIVE id (shared links,
  // older deep links) — the stores above resolve projectIds only, so a real
  // /compose/<creativeId> link used to bounce Home claiming the post was
  // deleted. Look the creative up and forward to its project; only when that
  // also misses is the post genuinely gone.
  const [creativeResolve, setCreativeResolve] = useState<'idle' | 'loading' | 'failed'>('idle')
  // The lookup machinery is PER-ID: when Case A promotes a creative URL to its
  // project URL (navigate replaces :id in place, no remount), the stale
  // attempted/failed flags from the OLD id made the NEW id render straight
  // into the hard-miss bounce — the composer reached /compose/<project> and
  // immediately re-bounced Home. Reset on every id change.
  const lastIdRef = useRef(id)
  useEffect(() => {
    if (lastIdRef.current === id) return
    lastIdRef.current = id
    setIdeaLookupAttempted(false)
    setIdeaLookupLoading(false)
    hardMissToastedRef.current = false
    setCreativeResolve('idle')
  }, [id])
  useEffect(() => {
    if (!hardMiss || !id) return
    if (hardMissToastedRef.current) return
    hardMissToastedRef.current = true
    setCreativeResolve('loading')
    // Look the CREATIVE up, never the route id: a /posts synthetic card routes
    // here as `creative:<id>`, and the raw id 404s on the creatives endpoint.
    apiCall(CreativeService.creativeControllerFindOneV1, null, creativeLookupId ?? id)
      .then(res => {
        // The endpoint wraps the entity: { creative: {...} } (see
        // creative-store's rollback fetch). Read both shapes defensively.
        const wrapped = res as { creative?: { projectId?: string }; projectId?: string } | null
        const projectId = wrapped?.creative?.projectId ?? wrapped?.projectId
        if (projectId) {
          navigate(`/compose/${projectId}`, { replace: true })
          return
        }
        setCreativeResolve('failed')
        toast.error('This post no longer exists. It may have been deleted.')
      })
      .catch(() => {
        setCreativeResolve('failed')
        toast.error('This post no longer exists. It may have been deleted.')
      })
  }, [hardMiss, id, creativeLookupId, navigate])

  // ─── One Post model: the bare URL is the Post view (THE editor); a
  // `:platform` segment is a platform PREVIEW. An invalid platform falls back
  // to the Post view (replace, so back-button stays clean).
  const activePlatform: LivePlatform | null =
    idea && paramPlatform && availablePlatforms.includes(paramPlatform) ? paramPlatform : null

  useEffect(() => {
    if (!idea || !paramPlatform) return
    if (availablePlatforms.includes(paramPlatform)) return
    navigate(`/compose/${idea.projectId}`, { replace: true })
  }, [idea, paramPlatform, availablePlatforms, navigate])

  // Seed the in-strip selection once per Post. Priority:
  //   1. `location.state.activePlatforms` — PostModal hand-off, what the user
  //      just staged but hasn't committed yet.
  //   2. `idea.confirmedPlatforms` — the server's committed publish set
  //      (IdeaCard entry, or returning-from-elsewhere).
  //   3. `defaultPlatformSeed` — fresh Post: active plan channels → connected
  //      channels, primary always included. While connections/plans load, a
  //      provisional [primary] keeps the strip non-empty (Schedule silently
  //      no-ops on an empty set); the ref stays unset so the real default
  //      lands once loaded — unless the user already touched the strip
  //      (handleAdd/dropFromSelection set the ref), which must never be
  //      clobbered.
  // Constrained to `candidatePlatforms` so stale entries don't ghost in.
  useEffect(() => {
    if (!idea) return
    if (selectionSeededFor.current === idea.projectId) return
    const navState = location.state as ComposerNavState | null
    const candidateSet = new Set(availablePlatforms)
    const staged = navState?.activePlatforms?.filter(p => candidateSet.has(p))
    const fromServer = idea.confirmedPlatforms?.filter(p => candidateSet.has(p))
    // A deep link to a variant tab must SHOW that tab: the strip renders the
    // selected set only (ComposerShell contract: active ∈ selected). Mirrors
    // handleEditPlatform's "always include the platform being edited".
    // Customized (pinned) platforms are user-owned copies — always represent
    // them in the strip, even when confirmedPlatforms hasn't caught up.
    const pinned = (Object.entries(siblings) as [LivePlatform, Creative | undefined][])
      .filter(([p, c]) => candidateSet.has(p) && !!c?.pinnedAt)
      .map(([p]) => p)
    const withImplied = (list: LivePlatform[]): LivePlatform[] => {
      const out = [...list]
      for (const p of pinned) if (!out.includes(p)) out.push(p)
      if (paramPlatform && candidateSet.has(paramPlatform) && !out.includes(paramPlatform)) {
        out.push(paramPlatform)
      }
      return out
    }
    let seed =
      staged && staged.length > 0 ? staged : fromServer && fromServer.length > 0 ? fromServer : null
    if (!seed) {
      if (connectionsLoading || !plansLoaded) {
        setSelectedPlatforms(prev =>
          prev.length === 0 ? withImplied([idea.primaryPlatform]) : prev
        )
        return
      }
      seed = defaultPlatformSeed(idea, connections).filter(p => candidateSet.has(p))
    }
    selectionSeededFor.current = idea.projectId
    setSelectedPlatforms(withImplied(seed))
  }, [
    idea,
    location.state,
    connectionsLoading,
    connections,
    plansLoaded,
    paramPlatform,
    siblings,
    availablePlatforms,
  ])

  // Selection drives derivation: every selected platform (plus the active
  // tab, so browsing a non-selected tab still paints) derives lazily through
  // the shared hook — at most one attempt per platform per mount, cache and
  // in-flight dedup inside previewSiblings. This replaced the old eager
  // all-candidates fan-out on composer open — see
  // playbook/product/multi-platform/DESIGN.md (Phase 0).
  const wantedPlatforms = useMemo(
    () =>
      activePlatform && !selectedPlatforms.includes(activePlatform)
        ? [...selectedPlatforms, activePlatform]
        : selectedPlatforms,
    [selectedPlatforms, activePlatform]
  )
  useDeriveSiblings(idea, wantedPlatforms)

  // ─── Strip handlers ──────────────────────────────────────────────────────
  const handleAdd = useCallback(
    (p: LivePlatform) => {
      if (!idea) return
      // Manual edit — the selection is now user-owned; the seed effect must
      // never overwrite it (relevant while connections/plans are still loading).
      selectionSeededFor.current = idea.projectId
      setSelectedPlatforms(prev => (prev.includes(p) ? prev : [...prev, p]))
      track(AnalyticsEvents.Composer.COMPOSER_PLATFORM_ADDED, {
        project_id: idea.projectId,
        platform: p,
      })
      // Adding a platform also switches the preview to it — the user just
      // chose it, that's the obvious next thing they want to look at.
      navigate(`/compose/${idea.projectId}/${p}`, { replace: true })
    },
    [idea, navigate, track]
  )

  const dropFromSelection = useCallback(
    (p: LivePlatform) => {
      if (!idea) return
      selectionSeededFor.current = idea.projectId
      setSelectedPlatforms(prev => prev.filter(x => x !== p))
      // If the removed platform was the active preview, move to the next
      // remaining one so the URL doesn't dangle. Navigate OUTSIDE the state
      // updater — React runs updaters during render, so navigate() inside one
      // throws "Cannot update BrowserRouter while rendering ComposerPage",
      // and that console.error lands in error tracking.
      if (p === activePlatform) {
        navigate(`/compose/${idea.projectId}`, { replace: true })
      }
    },
    [idea, activePlatform, selectedPlatforms, navigate]
  )

  const handleRemove = useCallback(
    (p: LivePlatform) => {
      if (!idea) return
      // The publish set can't go empty — Schedule/Post-now silently no-op on
      // an empty set, leaving a live-looking CTA that does nothing.
      if (selectedPlatforms.length <= 1) {
        toast('Keep at least one platform. Add another before removing this one.')
        return
      }
      const sibling = siblings[p]
      const wasScheduled = !!sibling?.scheduledAt

      if (wasScheduled && sibling) {
        setRemoveConfirm({ platform: p, siblingId: sibling.id })
        return
      }

      track(AnalyticsEvents.Composer.COMPOSER_PLATFORM_REMOVED, {
        project_id: idea.projectId,
        platform: p,
        was_scheduled: false,
      })
      dropFromSelection(p)
    },
    [idea, siblings, selectedPlatforms, dropFromSelection, track]
  )

  const confirmRemoveScheduled = useCallback(async () => {
    if (!idea || !removeConfirm) return
    const { platform, siblingId } = removeConfirm
    try {
      await useCalendarStore.getState().unscheduleCreative(siblingId)
    } catch (err) {
      console.error('composer: unschedule before remove failed', err)
    }
    track(AnalyticsEvents.Composer.COMPOSER_PLATFORM_REMOVED, {
      project_id: idea.projectId,
      platform,
      was_scheduled: true,
    })
    setRemoveConfirm(null)
    dropFromSelection(platform)
  }, [idea, removeConfirm, dropFromSelection, track])

  // ─── Active creative (Post mode → primary or sibling) ───────────────────
  // Composer is project-mode only. If we don't have an idea yet, we're either
  // still resolving (legacy /compose/:creativeId hits auto-promote via loadIdea)
  // or we hard-missed and will Navigate home below.
  const creativeForPlatform = (p: LivePlatform): Creative | null =>
    projectCreatives.find(c => creativeTypeToPlatform(c.type) === p) ?? null
  // One Post model: the Post view (activePlatform null) displays the
  // canonical; a platform view displays that platform's derived adaptation.
  const activeCreative = idea
    ? activePlatform === null
      ? (idea.primaryCreative ?? projectCreatives[0] ?? null)
      : activePlatform === idea.primaryPlatform
        ? (idea.primaryCreative ?? creativeForPlatform(activePlatform))
        : (siblings[activePlatform] ?? creativeForPlatform(activePlatform))
    : null

  // Platform tabs render the sibling in the same canvas surface; sections
  // carry the contract (VariantModeProvider). Customized = pinned in the DB
  // or unlocked through the gate this session. The PRIMARY platform's tab is
  // always "customized": its copy IS the post's, so editing it edits the post.
  const activeIsCustomized =
    !!idea &&
    !!activePlatform &&
    (activePlatform === idea.primaryPlatform ||
      !!activeCreative?.pinnedAt ||
      customizeUnlocked.has(activePlatform))
  const displayCreative = activeCreative

  // Customized-variant state for the strip: a pinned variant was hand-edited
  // and stopped auto-updating from the post; "behind" means the post's
  // canonical changed since this variant last derived.
  const activeIsPinnedVariant =
    !!idea && !!activeCreative && !!activePlatform && activePlatform !== idea.primaryPlatform
      ? (activeCreative.pinnedAt ?? null)
      : null
  const activeBehind =
    !!activeIsPinnedVariant &&
    (idea?.contentRevision ?? 0) > (activeCreative?.derivedFromRevision ?? 0)

  const handleResetToDerived = useCallback(async () => {
    if (!idea || !activeCreative || !activePlatform) return
    setResetConfirmOpen(false)
    try {
      await apiCall(CreativeService.creativeControllerRederiveVariantV1, null, activeCreative.id)
      const revision = idea.contentRevision ?? 0
      useIdeaStore
        .getState()
        .markSiblingUnpinnedLocally(idea.projectId, activeCreative.id, revision)
      // Re-arm the customize gate: without this the session unlock kept the
      // text editable and the next keystroke silently re-pinned, bypassing
      // the explicit-fork contract the reset just restored.
      setCustomizeUnlocked(prev => {
        if (!activePlatform || !prev.has(activePlatform)) return prev
        const next = new Set(prev)
        next.delete(activePlatform)
        return next
      })
      useCreativeStore.setState(state => ({
        creatives: state.creatives.map(c =>
          c.id === activeCreative.id ? { ...c, pinnedAt: null, derivedFromRevision: revision } : c
        ),
      }))
      toast.success(`${labelForPlatform(activePlatform)} will match the post again. Updating…`)
    } catch (err) {
      console.error('Reset to derived failed:', err)
      toast.error('Could not reset this platform. Please try again.')
    }
  }, [idea, activeCreative, activePlatform])

  // ─── Analytics ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeCreative) return
    const navSource = (location.state as { source?: string } | null)?.source
    const source =
      navSource ||
      (activeCreative.autopilotId
        ? 'autopilot'
        : activeCreative.isSuggestion
          ? 'suggestion'
          : 'prompt')
    track(AnalyticsEvents.Composer.COMPOSER_VIEWED, {
      creative_id: activeCreative.id,
      creative_type: activeCreative.type,
      source,
      ...(idea ? { project_id: idea.projectId, platform: activePlatform ?? 'post' } : {}),
    })
  }, [activeCreative?.id, activePlatform, idea?.projectId]) // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Render ──────────────────────────────────────────────────────────────
  if (!id) return <Navigate to="/" replace />

  // Loading: creatives list fetch in flight, or we're awaiting the loadIdea
  // round-trip. `ideaLookupLoading` covers the window between firing loadIdea
  // and the response landing — without it, the page renders one frame without
  // an `idea` and bounces home via the "hard miss" branch below.
  // An id change (Case A promoting /compose/<creativeId> to the project URL)
  // renders once BEFORE the per-id reset effect clears the previous id's
  // lookup flags — without this, that render saw attempted=true, skipped the
  // spinner, and hit the hard-miss bounce. Reading the ref here is the
  // standard previous-value comparison; the reset effect updates it.
  const idJustChanged = lastIdRef.current !== id
  const stillResolving =
    !idea && (loading || !loaded || ideaLookupLoading || !ideaLookupAttempted || idJustChanged)

  if (stillResolving) {
    return (
      <div className="flex h-full items-center justify-center bg-bg">
        <Loader2 size={24} className="animate-spin text-paper-faint" />
      </div>
    )
  }

  // The display document hasn't landed yet (orphaned primary, or a customized
  // sibling still hydrating). Preview mode never lands here — the preview
  // pane renders its own skeleton for a still-deriving sibling.
  if (idea && !displayCreative) {
    // Never hand a null creative to ComposerHeader (it reads creative.name and
    // crashes). The primary creative can be null when it was deleted but the
    // project + siblings survive (orphaned post). Fall back to any project
    // creative for the header; if there's truly none, render a bare spinner.
    const headerCreative = idea.primaryCreative ?? projectCreatives[0] ?? null
    return (
      <div className="flex h-full flex-col bg-bg">
        {headerCreative && (
          <ComposerHeader
            creative={headerCreative}
            postContext={{ idea, activePlatform: activePlatform! }}
          />
        )}
        <div className="flex flex-1 items-center justify-center bg-bg-2">
          <Loader2 size={24} className="animate-spin text-paper-faint" />
        </div>
      </div>
    )
  }

  // Hard miss: nothing matched after all retries.
  if (!displayCreative) {
    // Zombie render: Case A just promoted this creative id to its project URL
    // and a state flush re-renders the OLD id once before the router param
    // updates. Returning the bounce here would OVERRIDE the in-flight promote
    // navigation (the monkey's /compose/<creativeId> → Home). Hold instead.
    // Compare against the BARE creative id: a /posts synthetic card routes here
    // as `creative:<id>`, and the ref holds the id without the prefix, so the
    // raw route id never matched and every "Edit this post" on a synthetic
    // card (an autopilot post whose slot lapsed, a standalone creative) bounced
    // Home after the promote had already landed (David E, 2026-09-23).
    if (promotedFromCreativeIdRef.current === creativeLookupId) {
      return (
        <div className="flex h-full items-center justify-center bg-bg">
          <Loader2 size={24} className="animate-spin text-paper-faint" />
        </div>
      )
    }
    return <Navigate to="/" replace />
  }

  // Post mode → render with tabs.
  if (idea) {
    const removeConfirmLabel = removeConfirm ? labelForPlatform(removeConfirm.platform) : ''
    const stagedScheduledAt = (location.state as ComposerNavState | null)?.scheduledAt
    return (
      <div className="flex h-full flex-col">
        <ComposerHeader
          creative={displayCreative}
          postContext={{ idea, activePlatform: activePlatform ?? idea.primaryPlatform }}
          selectedPlatforms={selectedPlatforms}
          stagedScheduledAt={stagedScheduledAt}
        />
        <ComposerShell
          creative={displayCreative}
          chatCreative={idea.primaryCreative ?? displayCreative}
          projectId={idea.projectId}
          platformTabs={{
            candidates: availablePlatforms,
            selected: selectedPlatforms,
            primary: idea.primaryPlatform,
            active: activePlatform,
            postActive: activePlatform === null,
            shape: idea.shape,
            customizedPlatforms: selectedPlatforms.filter(
              p => p !== idea.primaryPlatform && !!siblings[p]?.pinnedAt
            ),
            connections,
            activePinnedAt: activeIsPinnedVariant,
            activeBehind,
            onSelectPost: () => navigate(`/compose/${idea.projectId}`, { replace: true }),
            onChange: p => navigate(`/compose/${idea.projectId}/${p}`, { replace: true }),
            onAdd: handleAdd,
            onRemove: handleRemove,
            onResetToDerived: () => setResetConfirmOpen(true),
          }}
          variantView={
            activePlatform
              ? {
                  platform: activePlatform,
                  platformLabel: labelForPlatform(activePlatform),
                  customized: activeIsCustomized,
                  onCustomizeRequest: () => setCustomizeGateOpen(true),
                  onGoToPost: () => navigate(`/compose/${idea.projectId}`, { replace: true }),
                }
              : undefined
          }
        />

        {/* The explicit customize gate: nobody forks a platform's copy by accident. */}
        <Dialog open={customizeGateOpen} onOpenChange={setCustomizeGateOpen}>
          <DialogContent size="sm" variant="dark" withCloseButton>
            <DialogHeader>
              <DialogTitle variant="dark">
                Customize {activePlatform ? labelForPlatform(activePlatform) : ''}?
              </DialogTitle>
              <DialogDescription variant="dark">
                Customizing gives this platform its own caption that stops auto-updating when you
                edit the post. You can reset it to match the post anytime.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                variant="t3-primary"
                size="t3-md"
                onClick={() => {
                  setCustomizeGateOpen(false)
                  if (activePlatform) {
                    setCustomizeUnlocked(prev => {
                      const next = new Set(prev)
                      next.add(activePlatform)
                      return next
                    })
                  }
                }}
                className="w-full"
              >
                Customize
              </Button>
              <Button
                variant="t3-tertiary"
                size="t3-md"
                onClick={() => setCustomizeGateOpen(false)}
                className="w-full"
              >
                Keep in sync
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset-to-derived confirmation — discards the variant's customizations. */}
        <Dialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
          <DialogContent size="sm" variant="dark" withCloseButton>
            <DialogHeader>
              <DialogTitle variant="dark">
                Reset {activePlatform ? labelForPlatform(activePlatform) : ''} to match the post?
              </DialogTitle>
              <DialogDescription variant="dark">
                Your customizations on this platform will be replaced with a fresh adaptation of the
                post. Future edits to the post will update it again automatically.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                variant="t3-primary"
                size="t3-md"
                onClick={() => void handleResetToDerived()}
                className="w-full"
              >
                Reset it
              </Button>
              <Button
                variant="t3-tertiary"
                size="t3-md"
                onClick={() => setResetConfirmOpen(false)}
                className="w-full"
              >
                Keep my edits
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Remove-scheduled confirmation. Reuses the dark Dialog. */}
        <Dialog
          open={!!removeConfirm}
          onOpenChange={open => {
            if (!open) setRemoveConfirm(null)
          }}
        >
          <DialogContent size="sm" variant="dark" withCloseButton>
            <DialogHeader>
              <DialogTitle variant="dark">Remove {removeConfirmLabel}?</DialogTitle>
              <DialogDescription variant="dark">
                {removeConfirmLabel} is scheduled. Removing it will unschedule the post on this
                platform.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <Button
                variant="t3-primary"
                size="t3-md"
                onClick={() => void confirmRemoveScheduled()}
                className="w-full"
              >
                Unschedule and remove
              </Button>
              <Button
                variant="t3-tertiary"
                size="t3-md"
                onClick={() => setRemoveConfirm(null)}
                className="w-full"
              >
                Keep it
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    )
  }

  // Hold the bounce while the creative-id fallback lookup is in flight —
  // navigating away mid-resolve would kill the redirect to the real project.
  if (hardMiss && creativeResolve !== 'failed') {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-acid" />
      </div>
    )
  }

  // Composer is project-mode only. Reaching here means resolution failed —
  // bounce home rather than render an orphan creative.
  return <Navigate to="/" replace />
}
